// Centralised API helper for the Onboarding section.
// All future fetch calls should go through `apiCall` so error handling stays consistent.
// Returns parsed JSON on success, throws a friendly Error on failure.

import React from 'react';
import { toast } from 'react-toastify';
import { BACKEND_API_URL } from '../../../config';
import { requestErrorShownSince } from '../../../errors/errorHandling';

// Strip the dynamic id segment so /projects/1/ and /projects/2/ collapse into
// one toastId — prevents the same endpoint from stacking 4 toasts when a tab
// fires parallel requests that all fail.
function toastKeyFor(path, method) {
  const base = path.split('?')[0].replace(/\/\d+(\/|$)/g, '/:id$1');
  return `api:${method}:${base}`;
}

// Keys the API uses for messages that aren't tied to a single field.
// `success` is the boolean flag every error envelope carries (see
// api/exception_handlers.py) — it's status, not a message, so it must never
// reach the toast as a "Success: false" bullet.
const TOP_LEVEL_KEYS = new Set(['detail', 'message', 'error', 'non_field_errors', 'success']);

// The error envelope repeats the DRF payload under `errors` *and* spreads its
// field keys at the top level, so reading both would print every message twice.
// `_error_response()` in gamify/views.py is the one shape where `errors` is the
// only copy, so it's used as a fallback rather than skipped outright.
const NESTED_ERRORS_KEY = 'errors';

// Never dump more than this many bullets into one toast.
const MAX_MESSAGES = 6;

// Field names that don't read well after a plain underscore→space swap.
// Mapping a key to '' keeps its messages attached to the parent label.
const FIELD_LABELS = {
  non_field_errors: '',
  detail: '',
  external_app_url: 'App URL',
  external_app_name: 'App name',
  department_code: 'Department',
  document_upload_name: 'Document name',
  requires_document_upload: 'Document upload',
  is_document_upload_mandatory: 'Mandatory document upload',
  allows_multiple_document_uploads: 'Multiple document uploads',
};

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

function humanizeField(field) {
  if (field in FIELD_LABELS) return FIELD_LABELS[field];
  const words = String(field).replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// "Tasks" → "Task" so an indexed row reads "Task 2", not "Tasks 2".
function singularize(label) {
  if (/ies$/i.test(label)) return `${label.slice(0, -3)}y`;
  if (/(ss|us)$/i.test(label)) return label;
  if (/s$/i.test(label)) return label.slice(0, -1);
  return label;
}

function joinLabel(prefix, label) {
  if (!label) return prefix;
  return prefix ? `${prefix} › ${label}` : label;
}

// "Tasks" + index 1 → "Task 2"; keeps any parent segments intact.
function indexedLabel(prefix, index) {
  if (!prefix) return `Item ${index + 1}`;
  const segments = prefix.split(' › ');
  segments[segments.length - 1] = `${singularize(segments[segments.length - 1])} ${index + 1}`;
  return segments.join(' › ');
}

// Walk a DRF error payload and collect readable "Label: message" lines.
// DRF nests arbitrarily deep, so this has to recurse — the shape that used to
// print "[object Object]" is a nested list serializer:
//   { tasks: [ {}, { estimated_minutes: ["must be at least 1."] } ] }
// which comes out as "Task 2 › Estimated minutes: must be at least 1."
function collectErrors(value, prefix, out) {
  if (value === null || value === undefined) return;

  if (typeof value === 'string') {
    const msg = value.trim();
    if (msg) out.push(prefix ? `${prefix}: ${msg}` : msg);
    return;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    out.push(prefix ? `${prefix}: ${value}` : String(value));
    return;
  }

  if (Array.isArray(value)) {
    // Two shapes land here:
    //   ["msg", "msg"]            — plain messages for `prefix`
    //   [{}, { field: [...] }]    — one entry per row of a nested list
    //                               serializer; `{}` means that row was fine
    const rowShaped = value.some(isPlainObject);
    value.forEach((item, i) => {
      if (isPlainObject(item) && Object.keys(item).length === 0) return;
      collectErrors(item, rowShaped ? indexedLabel(prefix, i) : prefix, out);
    });
    return;
  }

  if (isPlainObject(value)) {
    Object.entries(value).forEach(([field, val]) => {
      collectErrors(val, joinLabel(prefix, humanizeField(field)), out);
    });
  }
}

// Turn a DRF error payload into a de-duped list of human-readable messages.
function drfErrorMessages(payload) {
  if (!payload) return [];
  if (typeof payload === 'string') return payload.trim() ? [payload.trim()] : [];

  const parts = [];

  if (isPlainObject(payload)) {
    // Non-field / top-level messages first — they're the headline.
    collectErrors(payload.non_field_errors, '', parts);
    for (const key of ['detail', 'message', 'error']) {
      if (typeof payload[key] === 'string' && payload[key].trim()) parts.push(payload[key].trim());
    }
    const fieldParts = [];
    for (const [field, val] of Object.entries(payload)) {
      if (TOP_LEVEL_KEYS.has(field) || field === NESTED_ERRORS_KEY) continue;
      collectErrors(val, humanizeField(field), fieldParts);
    }
    // Only fall back to `errors` when the top level carried no field messages,
    // so the duplicated copy stays invisible.
    if (fieldParts.length === 0) {
      collectErrors(payload[NESTED_ERRORS_KEY], '', fieldParts);
    }
    parts.push(...fieldParts);
  } else {
    collectErrors(payload, '', parts);
  }

  return [...new Set(parts)];
}

// Cap the list so a 30-task template can't produce an unreadable wall of text.
function capMessages(messages) {
  if (messages.length <= MAX_MESSAGES) return messages;
  const extra = messages.length - MAX_MESSAGES;
  return [...messages.slice(0, MAX_MESSAGES), `…and ${extra} more ${extra === 1 ? 'issue' : 'issues'}`];
}

function failedVerb(method) {
  if (method === 'GET') return 'load';
  if (method === 'DELETE') return 'delete';
  return 'save';
}

// Returns { title, messages } so the toast can render a headline plus a bullet
// list. `payload` is the parsed JSON body from the API response (may be null).
function friendlyMessage(err, status, payload, method) {
  if (err?.name === 'TypeError') {
    return { title: 'Network error', messages: ['Please check your connection and try again.'] };
  }
  if (status === 401) {
    return { title: 'Session expired', messages: ['Please refresh and sign in again.'] };
  }
  // A 403 is an authorization problem, not necessarily a dead session. The API
  // explains why (missing gamify department role, inactive player, Super
  // Admin-only route), so showing "Session expired" would send the user off to
  // re-login for something a re-login can't fix.
  if (status === 403) {
    const [reason] = drfErrorMessages(payload);
    if (!reason) {
      return { title: 'Session expired', messages: ['Please refresh and sign in again.'] };
    }
    // DRF still uses 403 when credentials are missing entirely — there,
    // signing in again really is the fix.
    if (/credential|not authenticated|authentication/i.test(reason)) {
      return { title: 'Session expired', messages: ['Please refresh and sign in again.'] };
    }
    return { title: 'Access denied', messages: [reason] };
  }
  if (status === 404) {
    return { title: 'Not found', messages: ['The item may have been removed.'] };
  }
  if (status >= 500) {
    return { title: 'Server error', messages: ['Please try again in a moment.'] };
  }

  // Prefer the response body — DRF validation errors live here and are much
  // more specific than the generic "Request failed (400)" from err.message.
  const messages = drfErrorMessages(payload);
  if (messages.length === 1) {
    return { title: messages[0], messages: [] };
  }
  if (messages.length > 1) {
    return {
      title: `Couldn't ${failedVerb(method)} — ${messages.length} issues to fix`,
      messages: capMessages(messages),
    };
  }

  const raw = err?.message || 'Something went wrong';
  const [t, ...rest] = raw.split(/\s+—\s+|\s+-\s+|: /);
  const detail = rest.join(' ').trim();
  return detail ? { title: t.trim(), messages: [detail] } : { title: raw, messages: [] };
}

// Inline error icon — no extra dependency, matches lucide AlertCircle.
function ErrorIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function ErrorToast({ title, messages }) {
  const list = messages || [];
  return (
    <div className="rize-toast-body">
      <span className="rize-toast-body__icon"><ErrorIcon /></span>
      <div className="rize-toast-body__text">
        <p className="rize-toast-body__title">{title}</p>
        {list.length === 1 && <p className="rize-toast-body__detail">{list[0]}</p>}
        {list.length > 1 && (
          <ul className="rize-toast-body__list">
            {list.map((msg, i) => (
              <li key={`${i}-${msg}`}>{msg}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Show a client-side validation error using the same layout as API errors, so
 * "fix this before saving" looks identical whether we caught it or the server did.
 *
 * @param {string} title            - headline, e.g. "Couldn't save — 2 issues to fix"
 * @param {string[]} [messages]     - optional bullet list of specific problems
 * @param {object} [options]        - extra react-toastify options (e.g. toastId)
 */
/**
 * Catch handler for a rejected thunk — create it right where the thunk is
 * dispatched: `.unwrap().catch(showRejectedError())`. It skips the toast when
 * a request made by this action already failed (the global fetch observer
 * toasted that), so a failed save shows one message, not two.
 */
export function showRejectedError() {
  const startedAt = Date.now();
  return (err) => {
    if (requestErrorShownSince(startedAt)) return;
    const message = typeof err === 'string'
      ? err
      : (err?.message || err?.error || 'Something went wrong. Please try again.');
    showValidationError(message);
  };
}

export function showValidationError(title, messages = [], options = {}) {
  const list = capMessages([...new Set(messages.filter(Boolean))]);
  toast.error(<ErrorToast title={title} messages={list} />, {
    autoClose: Math.min(5000 + list.length * 1200, 12000),
    closeOnClick: true,
    pauseOnHover: true,
    icon: false,
    className: 'rize-toast',
    ...options,
  });
}

/**
 * @param {string} path        - e.g. '/api/admin/projects'
 * @param {object} options
 *   - method:   GET | POST | PUT | PATCH | DELETE
 *   - token:    bearer token (from state.admin.token)
 *   - body:     request body (will be JSON.stringified) — mutually exclusive with formData
 *   - formData: FormData object for multipart/form-data requests (e.g. image uploads)
 *   - silent:   if true, don't auto-toast errors (caller will handle)
 */
export async function apiCall(path, { method = 'GET', token, body, formData, silent = false } = {}) {
  let status = 0;
  let responsePayload = null;
  try {
    // When formData is provided, let the browser set Content-Type with the
    // correct boundary. For JSON, set it explicitly.
    const headers = { Authorization: token ? `Bearer ${token}` : '' };
    let requestBody;
    if (formData) {
      requestBody = formData;
    } else if (body !== undefined && body !== null) {
      headers['Content-Type'] = 'application/json';
      requestBody = JSON.stringify(body);
    }

    const response = await fetch(`${BACKEND_API_URL}${path}`, {
      method,
      headers,
      body: requestBody,
      // apiCall renders its richer field-level error below; keep the global
      // observer from displaying the same failure a second time.
      errorToast: false,
    });
    status = response.status;

    // Always try to parse JSON — we need the body for both success and error
    // cases. Non-JSON responses (204 No Content, file downloads) are fine: the
    // .catch(() => null) below keeps responsePayload as null.
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      responsePayload = await response.json().catch(() => null);
    }

    if (!response.ok) {
      // Throw a sentinel so the catch block can reach responsePayload via
      // closure. The message is intentionally generic here — friendlyMessage
      // will replace it with the DRF payload details when available.
      throw new Error(`Request failed (${response.status})`);
    }

    return responsePayload;
  } catch (err) {
    // Pass the parsed payload so field-level DRF errors can be surfaced.
    const { title, messages } = friendlyMessage(err, status, responsePayload, method);
    const flatMessage = messages.length ? `${title} — ${messages.join(' · ')}` : title;

    if (!silent) {
      // toastId dedupes — same endpoint won't stack multiple error toasts.
      toast.error(<ErrorToast title={title} messages={messages} />, {
        toastId: toastKeyFor(path, method),
        // Give people time to actually read a multi-line validation list.
        autoClose: Math.min(5000 + messages.length * 1200, 12000),
        closeOnClick: true,
        pauseOnHover: true,
        icon: false,
        className: 'rize-toast',
      });
    }
    throw new Error(flatMessage);
  }
}
